import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';

interface OrderItem {
  slug: string;
  qty: number;
}

interface SavedOrder {
  id: string;
  items: OrderItem[];
  contact: { fullName: string; phone: string; email?: string };
  shipping: {
    address1: string;
    address2?: string;
    city: string;
    area?: string;
    postalCode?: string;
    deliveryNote?: string;
  };
  payment: { method: string };
  subtotal: number;
  shippingCost: number;
  total: number;
  createdAt: string;
}

const ORDER_STORAGE_KEY = 'rxk-orders';

function findOrder(id: string): SavedOrder | null {
  try {
    const raw = localStorage.getItem(ORDER_STORAGE_KEY);
    const orders: SavedOrder[] = raw ? JSON.parse(raw) : [];
    return orders.find((o) => o.id === id) ?? null;
  } catch {
    return null;
  }
}

export default function OrderConfirmationPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';

  const order = id ? findOrder(id) : null;

  if (!order) {
    return (
      <>
        <SharedNavbar />
        <div className="w-full min-h-screen bg-white flex items-center justify-center pt-24 pb-20">
          <div className="text-center flex flex-col gap-4">
            <h1 className="font-serif text-2xl font-bold text-[#2A2A2A]">{t('order.notFound', 'Order not found')}</h1>
            <button
              onClick={() => navigate(prefix + '/products')}
              className="w-full sm:w-auto px-6 py-2.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
            >
              {t('common.continueShopping')}
            </button>
          </div>
        </div>
        <SharedFooter />
      </>
    );
  }

  const deliveryDays = order.shipping.city === 'Dhaka' ? '2-3' : '5-7';

  return (
    <>
      <SharedNavbar />
      <div className="w-full min-h-screen bg-[#FAFAF9] pt-24 pb-16">
        <div className="max-w-xl mx-auto px-6 md:px-12 py-10">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-[#E8E8E6] flex flex-col items-center text-center gap-6">
            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-[#7B1113]/10 flex items-center justify-center">
              <i className="ri-check-line text-3xl text-[#7B1113]"></i>
            </div>

            <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#2A2A2A]">
              {t('order.thankYou', 'Thank you for your order!')}
            </h1>

            <div className="flex flex-col gap-1">
              <span className="text-sm text-[#8A8A8A] font-light">
                {t('order.orderId', 'Order ID')}: <span className="font-medium text-[#2A2A2A]">#{order.id}</span>
              </span>
              <span className="text-xs text-[#8A8A8A] font-light">
                {t('order.estimatedDelivery', 'Estimated delivery')}: {deliveryDays} {t('order.businessDays', 'business days')}
                {order.shipping.city !== 'Dhaka' && ` (${t('order.outsideDhaka', 'Outside Dhaka')})`}
              </span>
            </div>

            {/* Order details table */}
            <div className="w-full border-t border-[#E8E8E6] pt-6 mt-2">
              <h3 className="text-xs tracking-[0.12em] uppercase font-medium text-[#2A2A2A] mb-4 text-left">
                {t('order.orderDetails', 'Order Details')}
              </h3>
              <div className="flex flex-col gap-3 text-left">
                {order.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-[#2A2A2A]">
                      {item.slug === '3-step-set' ? '3-Step Daily Treatment Set' : item.slug} × {item.qty}
                    </span>
                    <span className="text-sm font-medium text-[#2A2A2A]">
                      ৳{((order.total - order.shippingCost) / order.items.reduce((s, it) => s + it.qty, 0) * item.qty).toLocaleString()}
                    </span>
                  </div>
                ))}
                <div className="border-t border-[#E8E8E6] pt-3 flex items-center justify-between mt-1">
                  <span className="text-sm text-[#8A8A8A] font-light">{t('cart.subtotal')}</span>
                  <span className="text-sm text-[#2A2A2A]">৳{order.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#8A8A8A] font-light">{t('cart.shipping')}</span>
                  <span className="text-sm text-[#2A2A2A]">
                    {order.shippingCost === 0 ? t('cart.free') : `৳${order.shippingCost}`}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[#E8E8E6]">
                  <span className="text-base font-medium text-[#2A2A2A]">{t('cart.total')}</span>
                  <span className="text-lg font-serif font-bold text-[#2A2A2A]">৳{order.total.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="w-full flex flex-col gap-3 pt-4">
              <button
                onClick={() => navigate(prefix + '/products')}
                className="w-full py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('common.continueShopping')}
              </button>
              <div className="flex items-center justify-center gap-4 text-[10px] text-[#8A8A8A]">
                <span className="flex items-center gap-1">
                  <i className="ri-shield-check-line text-[#7B1113]"></i>
                  {t('order.authenticityGuaranteed', 'Authenticity Guaranteed')}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-qr-code-line text-[#7B1113]"></i>
                  {t('order.verifyQR', 'Verify with QR')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SharedFooter />
    </>
  );
}